import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ShoppingBag, ArrowLeft, Trash2, ShoppingCart, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = ({ user, onLogout, cart: cartProp }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [observations, setObservations] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  const isStaff = user && (user.role === 'admin' || user.role === 'manager');

  // Group cart items by model + color for size curve display
  const groupedCart = useMemo(() => {
    const groups = {};
    cart.forEach(item => {
      // Skip shipping items
      if (item.model_name === 'Portes') {
        groups['__shipping__'] = { isShipping: true, items: [item] };
        return;
      }
      
      const key = `${item.reference}-${item.model_name}-${item.color}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          reference: item.reference,
          model_name: item.model_name,
          color: item.color,
          image: item.image,
          price: item.price,
          items: [],
          sizes: []
        };
      }
      groups[key].items.push(item);
      groups[key].sizes.push({
        variant_id: item.variant_id,
        size: item.size,
        quantity: item.quantity,
        ean: item.ean
      });
    });
    
    // Sort sizes within each group
    Object.values(groups).forEach(group => {
      if (!group.isShipping) {
        group.sizes.sort((a, b) => {
          const sizeA = parseFloat(a.size) || 0;
          const sizeB = parseFloat(b.size) || 0;
          return sizeA - sizeB;
        });
      }
    });
    
    return Object.values(groups);
  }, [cart]);

  const toggleGroupExpand = (key) => {
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Load clients so admin/manager can place orders on behalf of a client
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'manager')) {
      const token = localStorage.getItem('token');
      axios
        .get(`${API}/admin/clients-for-order`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setClients(res.data || []))
        .catch(() => setClients([]));
    }
  }, [user]);

  // Update local cart state when prop changes
  useEffect(() => {
    if (cartProp && cartProp.length > 0) {
      setCart(cartProp);
    } else {
      loadCart();
    }
  }, [cartProp]);

  const loadCart = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const updateQuantity = (variantId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedCart = cart.map(item => 
      item.variant_id === variantId ? { ...item, quantity: newQuantity } : item
    );
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
  };

  const removeItem = (variantId) => {
    const updatedCart = cart.filter(item => item.variant_id !== variantId);
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    toast.success('Producto eliminado del carrito');
  };

  // Remove all items of a specific model+color group
  const removeGroup = (groupKey) => {
    const updatedCart = cart.filter(item => {
      const itemKey = `${item.reference}-${item.model_name}-${item.color}`;
      return itemKey !== groupKey;
    });
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    toast.success('Modelo eliminado del carrito');
  };

  // Calculate subtotal for a group
  const getGroupSubtotal = (group) => {
    return group.sizes.reduce((sum, s) => sum + (group.price * s.quantity), 0);
  };

  // Calculate total pairs for a group
  const getGroupTotalPairs = (group) => {
    return group.sizes.reduce((sum, s) => sum + s.quantity, 0);
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const needsShipping = () => {
    const total = calculateTotal();
    // Check if there's already a shipping item in cart
    const hasShippingItem = cart.some(item => item.model_name === 'Portes');
    return total < 150 && !hasShippingItem;
  };

  const addShippingToCart = () => {
    const shippingItem = {
      variant_id: 'SHIPPING',
      reference: 'SHIPPING',
      ean: 'SHIPPING',
      model_name: 'Portes',
      color: '',
      size: '',
      quantity: 1,
      price: 10,
      image: '',
      is_shipping: true
    };
    
    const updatedCart = [...cart, shippingItem];
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    toast.success('Portes añadidos al carrito (10€)');
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    // Check if shipping is needed
    if (needsShipping()) {
      toast.error('El pedido mínimo es de 150€ para portes pagados. Por favor añade más productos o acepta los portes de 10€.');
      return;
    }

    setLoading(true);

    try {
      // Ensure all cart items have required fields, including reference
      const validatedItems = cart.map(item => ({
        variant_id: item.variant_id,
        reference: item.reference || item.ean || 'N/A', // Fallback to EAN or N/A if reference missing
        ean: item.ean,
        model_name: item.model_name,
        color: item.color,
        size: item.size,
        quantity: item.quantity,
        price: item.price
      }));

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/orders`,
        { 
          items: validatedItems,
          delivery_date: deliveryDate || null,
          observations: observations.trim() || null,
          target_user_id: selectedClientId || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const assignedClient = clients.find((c) => c.id === selectedClientId);
      toast.success(
        assignedClient
          ? `¡Pedido realizado a nombre de ${assignedClient.business_name}!`
          : '¡Pedido realizado exitosamente!'
      );
      
      // Clear the cart completely
      localStorage.removeItem('cart');
      setCart([]);
      setObservations('');
      setDeliveryDate('');
      setSelectedClientId('');
      
      // Dispatch event to notify other components that cart was cleared
      window.dispatchEvent(new Event('cartUpdate'));
      
      navigate('/my-orders');
    } catch (error) {
      console.error('Error placing order:', error);
      
      // Handle validation errors properly - convert everything to string
      let errorMessage = 'Error al realizar el pedido. Por favor, inténtalo de nuevo.';
      
      try {
        if (error.response && error.response.data && error.response.data.detail) {
          const detail = error.response.data.detail;
          
          if (Array.isArray(detail)) {
            // Validation errors array
            const errors = detail.map(err => {
              const field = Array.isArray(err.loc) ? err.loc.join('.') : 'campo';
              const msg = err.msg || 'error';
              return field + ': ' + msg;
            });
            errorMessage = 'Error de validación: ' + errors.join(', ');
          } else if (typeof detail === 'string') {
            errorMessage = detail;
          } else {
            errorMessage = 'Error al procesar el pedido';
          }
        }
      } catch (parseError) {
        console.error('Error parsing error:', parseError);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-black border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center space-x-2 text-white hover:text-blue-400 transition">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al catálogo</span>
            </Link>
            <Link to="/" className="flex items-center space-x-3">
              <img src="/hi-tec-logo.png" alt="HI-TEC" className="h-10" />
              <span className="text-white text-xl font-bold">B2B</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Cart Content */}
      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-black mb-8">Carrito de Compras</h1>

        {cart.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
            <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-black mb-2">Tu carrito está vacío</h2>
            <p className="text-gray-600 mb-6">Agrega productos desde el catálogo</p>
            <Link to="/catalog">
              <Button data-testid="go-to-catalog-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                Ir al Catálogo
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items - Grouped by Model+Color */}
            <div className="lg:col-span-2 space-y-4">
              {groupedCart.map((group) => {
                // Shipping item - render normally
                if (group.isShipping) {
                  const item = group.items[0];
                  return (
                    <div 
                      key="shipping"
                      className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-bold text-amber-800">🚚 Portes</h3>
                          <p className="text-sm text-amber-600">Pedidos menores a €150</p>
                        </div>
                        <p className="text-xl font-bold text-amber-700">€{item.price.toFixed(2)}</p>
                      </div>
                    </div>
                  );
                }

                // Product group with size curve
                const isExpanded = expandedGroups[group.key] !== false; // Default expanded
                const totalPairs = getGroupTotalPairs(group);
                const subtotal = getGroupSubtotal(group);

                return (
                  <div 
                    key={group.key}
                    data-testid={`cart-group-${group.reference}`}
                    className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden"
                  >
                    {/* Group Header */}
                    <div 
                      className="p-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 cursor-pointer hover:bg-slate-50 transition"
                      onClick={() => toggleGroupExpand(group.key)}
                    >
                      <div className="flex items-center space-x-4">
                        {/* Product Image */}
                        <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          {group.image ? (
                            <img
                              src={group.image}
                              alt={group.model_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/100?text=No+Image';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ShoppingBag className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-black">{group.model_name}</h3>
                          <p className="text-sm text-blue-600 font-semibold">REF: {group.reference}</p>
                          <p className="text-gray-600 text-sm">Color: {group.color}</p>
                        </div>

                        {/* Summary */}
                        <div className="text-right">
                          <p className="text-sm text-slate-500">{totalPairs} pares</p>
                          <p className="text-xl font-bold text-blue-600">€{subtotal.toFixed(2)}</p>
                          <p className="text-xs text-slate-400">€{group.price.toFixed(2)} c/u</p>
                        </div>

                        {/* Expand/Collapse */}
                        <div className="text-slate-400">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>

                    {/* Size Curve Table - Collapsible */}
                    {isExpanded && (
                      <div className="p-4">
                        {/* Size Curve Header */}
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700">Curva de Tallas:</span>
                          <Button
                            data-testid={`remove-group-${group.reference}`}
                            onClick={(e) => { e.stopPropagation(); removeGroup(group.key); }}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar modelo
                          </Button>
                        </div>

                        {/* Size Curve Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-100">
                                <th className="px-3 py-2 text-left font-semibold text-slate-700">Talla</th>
                                <th className="px-3 py-2 text-center font-semibold text-slate-700">Cantidad</th>
                                <th className="px-3 py-2 text-right font-semibold text-slate-700">Subtotal</th>
                                <th className="px-3 py-2 w-10"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.sizes.map((sizeItem) => (
                                <tr key={sizeItem.variant_id} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="px-3 py-2 font-semibold text-slate-900">{sizeItem.size}</td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center justify-center space-x-2">
                                      <Button
                                        data-testid={`decrease-qty-${sizeItem.variant_id}`}
                                        onClick={(e) => { e.stopPropagation(); updateQuantity(sizeItem.variant_id, sizeItem.quantity - 1); }}
                                        variant="outline"
                                        size="sm"
                                        className="w-7 h-7 p-0"
                                      >
                                        -
                                      </Button>
                                      <span className="w-8 text-center font-semibold">{sizeItem.quantity}</span>
                                      <Button
                                        data-testid={`increase-qty-${sizeItem.variant_id}`}
                                        onClick={(e) => { e.stopPropagation(); updateQuantity(sizeItem.variant_id, sizeItem.quantity + 1); }}
                                        variant="outline"
                                        size="sm"
                                        className="w-7 h-7 p-0"
                                      >
                                        +
                                      </Button>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-right font-semibold text-blue-600">
                                    €{(group.price * sizeItem.quantity).toFixed(2)}
                                  </td>
                                  <td className="px-3 py-2">
                                    <Button
                                      data-testid={`remove-size-${sizeItem.variant_id}`}
                                      onClick={(e) => { e.stopPropagation(); removeItem(sizeItem.variant_id); }}
                                      variant="ghost"
                                      size="sm"
                                      className="w-7 h-7 p-0 text-red-400 hover:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-blue-50 font-semibold">
                                <td className="px-3 py-2 text-slate-700">TOTAL</td>
                                <td className="px-3 py-2 text-center text-slate-900">{totalPairs} pares</td>
                                <td className="px-3 py-2 text-right text-blue-700">€{subtotal.toFixed(2)}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-24">
                <h2 className="text-2xl font-bold text-black mb-6">Resumen del Pedido</h2>

                {/* Client selector - admin/manager can place orders on behalf of a client */}
                {isStaff && (
                  <div className="mb-6 bg-blue-50 border-2 border-blue-300 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-blue-900 mb-2">
                      👤 Asignar pedido a un cliente
                    </label>
                    <select
                      data-testid="client-select"
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className="w-full px-3 py-3 border-2 border-gray-300 rounded-lg bg-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">— Sin cliente (pedido propio) —</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.business_name}{c.customer_code ? ` (${c.customer_code})` : ''} · {c.order_count} pedidos
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-blue-800 mt-2">
                      {selectedClientId
                        ? 'El pedido se registrará a nombre del cliente seleccionado y recibirá su confirmación.'
                        : `Selecciona un cliente para grabar el pedido en su nombre (${clients.length} clientes).`}
                    </p>
                  </div>
                )}
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>€{getTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Artículos</span>
                    <span>{cart.reduce((sum, item) => sum + item.quantity, 0)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 flex justify-between text-xl font-bold text-black">
                    <span>Total</span>
                    <span data-testid="cart-total">€{getTotal().toFixed(2)}</span>
                  </div>
                </div>

                {/* Delivery Date Field */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    📅 Fecha de Entrega Deseada
                  </label>
                  <input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Selecciona cuándo deseas recibir el pedido. Para programación, mínimo según fecha de servicio.
                  </p>
                </div>

                {/* Observations Field */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Observaciones (Opcional)
                  </label>
                  <textarea
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    placeholder="Añade cualquier comentario o instrucción especial para tu pedido..."
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
                    rows="3"
                    maxLength="500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {observations.length}/500 caracteres
                  </p>
                </div>

                {/* Shipping Notice */}
                {needsShipping() && (
                  <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-4 mb-4">
                    <p className="text-orange-900 font-semibold mb-2">📦 Portes Necesarios</p>
                    <p className="text-sm text-orange-800 mb-3">
                      El pedido mínimo para portes pagados es de 150€. 
                      Tu pedido actual: <strong>€{calculateTotal().toFixed(2)}</strong>
                    </p>
                    <Button
                      onClick={addShippingToCart}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      size="sm"
                    >
                      Añadir Portes (10€)
                    </Button>
                  </div>
                )}

                {user && !isStaff && !user.approved ? (
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center">
                    <p className="text-yellow-900 font-semibold mb-2">⏳ Cuenta Pendiente de Aprobación</p>
                    <p className="text-sm text-yellow-800">
                      No puedes realizar pedidos hasta que tu cuenta sea aprobada por un administrador.
                    </p>
                  </div>
                ) : (
                  <>
                    <Button
                      data-testid="checkout-btn"
                      onClick={handleCheckout}
                      disabled={loading || needsShipping()}
                      className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg text-white disabled:bg-gray-400"
                    >
                      {loading ? 'Procesando...' : needsShipping() ? 'Añade portes o más productos' : 'Realizar Pedido'}
                    </Button>

                    {!needsShipping() && calculateTotal() >= 150 && (
                      <p className="text-xs text-green-600 text-center mt-2 font-semibold">
                        ✓ Portes pagados (pedido ≥ 150€)
                      </p>
                    )}

                    <p className="text-xs text-gray-500 text-center mt-4">
                      Recibirás una confirmación por email
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Cart;
